import type { PartialPaymentPolicy } from '@/types/partial-payment';

export interface DueDateWindow {
  start: string;
  end: string;
}

export function getDueDateWindowForSplitCount(
  policy: PartialPaymentPolicy,
  splitCount: number,
): DueDateWindow | null {
  const map = policy.due_date_window_by_split_count ?? {};
  const key = String(Math.max(1, splitCount));
  const entry = map[key] ?? map[splitCount as unknown as string];
  if (!entry?.start?.trim() || !entry?.end?.trim()) {
    return null;
  }
  const start = entry.start.trim().slice(0, 10);
  const end = entry.end.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return null;
  }
  if (start > end) {
    return null;
  }
  return { start, end };
}

function parseYmdToTime(s: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d.setHours(0, 0, 0, 0);
}

export function isDueDateWithinWindow(dueDate: string, window: DueDateWindow): boolean {
  const dueT = parseYmdToTime(dueDate);
  const startT = parseYmdToTime(window.start);
  const endT = parseYmdToTime(window.end);
  if (dueT == null || startT == null || endT == null) return false;
  return dueT >= startT && dueT <= endT;
}

export interface PaymentScheduleAmountRow {
  amount: string;
  due_date: string;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function getLateFeePercentForSplitCount(
  policy: PartialPaymentPolicy,
  splitCount: number,
): number {
  const map = policy.late_fee_by_split_count ?? {};
  const key = String(Math.max(1, splitCount));
  const v = map[key] ?? map[splitCount as unknown as string];
  return typeof v === 'number' ? v : parseFloat(String(v ?? 0)) || 0;
}

export function computeScheduleLateFees(
  policy: PartialPaymentPolicy,
  rows: PaymentScheduleAmountRow[],
): {
  percent: number;
  feesTotal: number;
  scheduledPrincipal: number;
  scheduledWithFees: number;
} {
  const count = rows.length;
  const percent = count > 0 ? getLateFeePercentForSplitCount(policy, count) : 0;
  let scheduledPrincipal = 0;
  let feesTotal = 0;
  for (const row of rows) {
    const amt = parseFloat(row.amount) || 0;
    scheduledPrincipal += amt;
    feesTotal += round2(amt * (percent / 100));
  }
  scheduledPrincipal = round2(scheduledPrincipal);
  feesTotal = round2(feesTotal);
  return {
    percent,
    feesTotal,
    scheduledPrincipal,
    scheduledWithFees: round2(scheduledPrincipal + feesTotal),
  };
}

/** Tur ana bedeli + planlanan vade farkları */
export function computeGrandTotalWithLateFees(tourTotal: number, scheduleFeesTotal: number): number {
  return round2(Math.max(0, tourTotal) + Math.max(0, scheduleFeesTotal));
}
